import { Component, OnInit, TemplateRef, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { CommonModule } from "@angular/common";
import { MatTableModule } from "@angular/material/table";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatDialog } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";

import { merge, Subject, of as observableOf } from "rxjs";
import { catchError, map, startWith, switchMap, takeUntil } from "rxjs/operators";

import { DialogComponent } from "../../../dialog/dialog.component";
import { UserFormComponent } from "../../../forms/create-account/user-form/user-form.component";
import { CoreService } from "../../../../services/core.service";
import { UserI } from '../../../../interfaces/core.interface';
import { MatSlideToggleModule } from "@angular/material/slide-toggle";


@Component({
  selector: 'app-users-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatIcon,
    MatPaginator,
    MatToolbarModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatSort,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    MatSlideToggleModule,
    UserFormComponent],

  templateUrl: './users-table.component.html',
  styleUrl: './users-table.component.scss'
})
export class UsersTableComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  @ViewChild("userFormTmpl", { static: true })
  userFormTmpl!: TemplateRef<any>;
  @ViewChild("confirmDeleteTmpl", { static: true })
  confirmDeleteTmpl!: TemplateRef<any>;

  private AllowNewUser: boolean = true;

  userSources: UserI[] = [];
  selectedUser: UserI = {} as UserI;
  dataSource = new MatTableDataSource<UserI>();
  displayedColumns: string[] = [
    "ID",
    "Email",
    "FirstName",
    "LastName",
    "Phone",
    "UserGroupID",
    "Company",
    "isActive",
    // "text_direction",
    "actions"
  ];

  selectOptions: { ID: number, Name: string }[] = [];
  showFilters: boolean = false;
  conditions = {
    Email: "",
    Password: "",
    FirstName: "",
    LastName: "",
    Phone: "",
    companyID: ""
  };

  loading: boolean = false;
  totalItems: number = 0;

  private destroy$ = new Subject<void>();
  private companyID: any = "";

  constructor(private coreService: CoreService, public dialog: MatDialog) { }

  buildQueryParams(): any {
    return {
      ...this.conditions,
      sortBy: this.sort?.active || '',
      sortOrder: this.sort?.direction || '',
      offset: this.paginator.pageIndex * this.paginator.pageSize,
      limit: this.paginator.pageSize
    };
  }

  ngOnInit(): void {
    this.companyID = document.getElementById('companysettingsfor')?.innerHTML;
    // this.coreService.getAllowAddUser(this.companyID).subscribe({
    //   next: (res) => {
    //     this.AllowNewUser = res.allowAddUser;
    //   },
    //   error: (err) => {
    //     console.error('Failed to load AllowNewUser', err);
    //   }
    // });

    this.coreService.getData(`/company/dropdown`).subscribe(data => {
      this.selectOptions = data;
    })
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // If the user changes the sort order, reset back to the first page.
    this.sort.sortChange
      .pipe(takeUntil(this.destroy$))  // Add this line
      .subscribe(() => (this.paginator.pageIndex = 0));

    const divContent = document.getElementById('companysettingsfor')?.innerHTML;

    merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        takeUntil(this.destroy$),  // Add this line
        switchMap(() => {
          this.loading = true;
          const params = this.buildQueryParams();
          return this.coreService.getData(`/msuser/${divContent}`, params)
            .pipe(catchError(() => observableOf(null)));
        }),
        map((data) => {
          this.loading = false;
          return data;
        })
      )
      .subscribe((data) => {
        this.dataSource = data.data;
        this.totalItems = data.total;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(); // Notify all subscriptions to complete
    this.destroy$.complete(); // Complete the observable
  }

  getUserGroupFields(params: { [key: string]: any }): void {
    const divContent = document.getElementById('companysettingsfor')?.innerHTML;
    this.coreService.getData(`/msuser/${divContent}`, params).pipe(
      takeUntil(this.destroy$)  // Ensure the subscription is unsubscribed on destroy
    ).subscribe({
      next: (data) => {
        this.totalItems = data.total;
        this.dataSource = data.data;
      },
      error: (error) => {
        console.error("Error fetching user:", error);
      },
    });
  }

  editUser(row: UserI): void {
    this.selectedUser = row;
    this.dialog.open(DialogComponent, {
      data: {
        content: this.userFormTmpl,
        header: "Edit User",
        cls: "!bg-slate-600 !text-white",
      },
      minWidth: "80vw",
      maxWidth: "700px",
    });
  }

  openDeleteUserConfirm(row: UserI): void {
    this.selectedUser = row;
    this.dialog.open(DialogComponent, {
      data: {
        content: this.confirmDeleteTmpl,
        header: "Delete User",
        cls: 'bg-red-500 !text-white'
      },
    });
  }

  deleteUser(): void {
    this.coreService
      .deleteData("msuser/destroy", String(this.selectedUser.ID))
      .subscribe(() => {
        this.dialog.closeAll();
        const offset = (this.paginator.pageIndex ?? 0) * this.paginator.pageSize;
        this.getUserGroupFields({
          offset: Number(offset),
          limit: this.paginator.pageSize,
          sortBy: this.sort.active ?? "",
          sortOrder: this.sort.direction ?? "",
        });
      });
  }

  addNewUser() {
    if (this.AllowNewUser) {
      this.selectedUser = {} as UserI;
      this.dialog.open(DialogComponent, {
        data: {
          content: this.userFormTmpl,
          header: "Add New User",
          cls: "!bg-slate-600 !text-white",
        },
        minWidth: "80vw",
        maxWidth: "700px",
      });
    } else {
      this.coreService.showSnackbar("You have exceed max number of users allowed by your economic plan.");
    }
  }

  onSubmitClbk(event: any) {
    this.dialog.closeAll();
    const offset = (this.paginator.pageIndex ?? 0) * this.paginator.pageSize;
    const divContent = document.getElementById('companysettingsfor')?.innerHTML;
    this.coreService.getData(`/msuser/${divContent}`, {
      offset: Number(offset),
      limit: this.paginator.pageSize,
      sortBy: this.sort.active ?? '',
      sortOrder: this.sort.direction ?? ''
    }).subscribe(data => {
      this.dataSource = data.data;
      this.totalItems = data.total;
    })
  }

  applyConditions() {
    this.paginator.pageIndex = 0;

    const params = {
      ...this.conditions,
      sortBy: this.sort.active ?? "",
      sortOrder: this.sort.direction ?? "",
      limit: this.paginator.pageSize,
      offset: 0,
    };

    this.paginator.pageIndex = 0;
    this.sort.sortChange.emit();
  }

  clearConditions() {
    this.conditions = {
      Email: "",
      Password: "",
      FirstName: "",
      LastName: "",
      Phone: "",
      companyID: ""
    };
  }

  onDirectionToggle(user: any, checked: boolean) {
    const newDir = checked ? "rtl" : "ltr";

    const oldDir = user.text_direction || "ltr";
    user.text_direction = newDir;
    user.__dirLoading = true;

    this.coreService.postData(`msuser/${user.ID}/direction`, {
      text_direction: newDir
    }).subscribe({
      next: (res: any) => {
        user.__dirLoading = false;
        if (res?.text_direction) user.text_direction = res.text_direction;
        this.coreService.showSnackbar(`Direction updated to ${user.text_direction.toUpperCase()}`);
      },
      error: (err: any) => {
        user.__dirLoading = false;
        user.text_direction = oldDir;
        console.error(err);
        this.coreService.showSnackbar("Failed to update direction");
      }
    });
  }
  onActiveToggle(user: any, checked: boolean) {
    const oldValue = user.isActive ?? 0;
    const newValue = checked ? 1 : 0;

    user.isActive = newValue;
    user.__activeLoading = true;

    this.coreService.postData(`msuser/${user.ID}/active`, {
      isActive: newValue
    }).subscribe({
      next: (res: any) => {
        user.__activeLoading = false;
        if (res?.isActive !== undefined) user.isActive = res.isActive;
        this.coreService.showSnackbar(`User is now ${user.isActive == 1 ? 'Active' : 'Inactive'}`);
      },
      error: (err: any) => {
        user.__activeLoading = false;
        user.isActive = oldValue;
        console.error(err);
        this.coreService.showSnackbar("Failed to update user status");
      }
    });
  }



}
